import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Database, 
  FileText, 
  BarChart3, 
  Play,
  Calendar,
  Clock,
  ArrowRight,
  RefreshCw,
  Workflow,
  GitBranch,
  Zap,
  Eye,
  Shield,
  Target,
  TestTube,
  Activity,
  Globe
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { apiService } from '../services/apiService';
import PipelineDiagram from '../components/PipelineDiagram';

const DataWorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useApp();

  // Task descriptions from PipelineDiagram
  const taskDescriptions: { [key: number]: string } = {
    1: 'Collect and process credit card data from Prime system',
    2: 'Generate synthetic data and continue modifying Workspace DB',
    3: 'Add functional test scenarios and continue modifying Workspace DB',
    4: 'Load from Workspace DB and push to multiple destination servers'
  };

  useEffect(() => {
    // Only load runs if they haven't been loaded yet
    if (state.runs.length === 0 && !state.isLoading) {
      loadRuns();
    }
  }, []);

  const loadRuns = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const runs = await apiService.getRuns();
      // Ensure runs is always an array and properly formatted
      const formattedRuns = Array.isArray(runs) 
        ? runs.map((run: any) => ({
            ...run,
            // Ensure all required properties exist
            id: run.id || run.run_id || '',
            name: run.name || run.id || run.run_id || 'Unnamed Run',
            run_id: run.run_id || run.id || '',
            timestamp: run.timestamp || run.created_at || '',
            created_at: run.created_at || run.timestamp || '',
            has_schema: run.has_schema !== undefined ? run.has_schema : !!run.schema,
            has_synthetic_data: run.has_synthetic_data !== undefined 
              ? run.has_synthetic_data 
              : !!(run.synthetic_csv_files && run.synthetic_csv_files.length > 0),
            has_input_data: run.has_input_data !== undefined 
              ? run.has_input_data 
              : !!(run.input_csv_files && run.input_csv_files.length > 0),
            has_validation: run.has_validation || false,
          }))
        : [];
      dispatch({ type: 'SET_RUNS', payload: formattedRuns });
    } catch (error) {
      console.error('Failed to load runs:', error);
      dispatch({ type: 'SET_RUNS', payload: [] });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const getStatusBadges = (run: any) => {
    if (!run || typeof run !== 'object') return [];
    const badges = [];
    if (run.has_schema || run.schema) badges.push({ label: 'Schema', color: 'success' });
    if (run.has_synthetic_data || (run.synthetic_csv_files && run.synthetic_csv_files.length > 0)) badges.push({ label: 'Data', color: 'info' });
    if (run.has_validation) badges.push({ label: 'Validation', color: 'warning' });
    if (run.has_input_data || (run.input_csv_files && run.input_csv_files.length > 0)) badges.push({ label: 'Input', color: 'accent' });
    return badges;
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    
    try {
      // Handle format: YYYYMMDD_HHMMSS (e.g., "20251229_152809")
      if (dateString.match(/^\d{8}_\d{6}$/)) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        const hour = dateString.substring(9, 11);
        const minute = dateString.substring(11, 13);
        const second = dateString.substring(13, 15);
        // Create ISO date string: YYYY-MM-DDTHH:MM:SS
        const isoDate = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
        const date = new Date(isoDate);
        if (isNaN(date.getTime())) {
          return 'N/A';
        }
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      // Try standard date parsing for ISO strings or other formats
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'N/A';
    }
  };

  // Ensure runs is always an array
  const runs = Array.isArray(state.runs) ? state.runs : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-windows-900 dark:text-gray-100">Welcome to Test Data Hub</h1>
          <p className="text-windows-600 dark:text-gray-400 mt-2">
            AI-powered synthetic data generation and validation system
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadRuns}
            className="btn-windows flex items-center space-x-2"
            disabled={state.isLoading}
          >
            <RefreshCw size={16} className={state.isLoading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="btn-windows-primary flex items-center space-x-2"
          >
            <Plus size={16} />
            <span>Create New System</span>
          </button>
          <button
            onClick={() => navigate('/create-pipeline')}
            className="btn-windows flex items-center space-x-2"
          >
            <GitBranch size={16} />
            <span>Create Pipeline</span>
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card-windows p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-windows-600 dark:text-gray-400">Total Tasks</p>
              <p className="text-2xl font-bold text-windows-900 dark:text-gray-100">{runs.length}</p>
            </div>
            <Database className="h-8 w-8 text-accent-600 dark:text-accent-400" />
          </div>
        </div>
        
        <div className="card-windows p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-windows-600 dark:text-gray-400">With Schema</p>
              <p className="text-2xl font-bold text-windows-900 dark:text-gray-100">
                {runs.filter(r => r.has_schema || r.schema).length}
              </p>
            </div>
            <FileText className="h-8 w-8 text-success-600 dark:text-success-400" />
          </div>
        </div>
        
        <div className="card-windows p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-windows-600 dark:text-gray-400">With Data</p>
              <p className="text-2xl font-bold text-windows-900 dark:text-gray-100">
                {runs.filter(r => r.has_synthetic_data || (r.synthetic_csv_files && r.synthetic_csv_files.length > 0)).length}
              </p>
            </div>
            <BarChart3 className="h-8 w-8 text-warning-600 dark:text-warning-400" />
          </div>
        </div>
        
        <div className="card-windows p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-windows-600 dark:text-gray-400">Validated</p>
              <p className="text-2xl font-bold text-windows-900 dark:text-gray-100">
                {runs.filter(r => r.has_validation || false).length}
              </p>
            </div>
            <Play className="h-8 w-8 text-error-600 dark:text-error-400" />
          </div>
        </div>
      </div>

      {/* Pipeline Diagram */}
      <PipelineDiagram />
      
      {/* Create Custom Task Pipeline */}
      <div className="card-windows">
        <div className="p-6 border-b border-windows-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-windows-900 dark:text-gray-100 flex items-center space-x-2">
            <Workflow className="h-5 w-5 text-accent-600 dark:text-accent-400" />
            <span>Create Custom Task Pipeline</span>
          </h2>
          <p className="text-windows-600 dark:text-gray-400 mt-1">
            Build custom data processing pipelines by combining different tasks and workflows
          </p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-center p-6 bg-gradient-to-br from-accent-50 to-blue-50 dark:from-accent-900/20 dark:to-blue-900/20 rounded-lg border border-accent-200 dark:border-accent-700">
              <div className="p-3 bg-accent-100 dark:bg-accent-900/30 rounded-lg w-fit mx-auto mb-4">
                <GitBranch className="h-8 w-8 text-accent-600 dark:text-accent-400" />
              </div>
              <h3 className="text-lg font-semibold text-windows-900 dark:text-gray-100 mb-2">Task Pipeline Builder</h3>
              <p className="text-windows-600 dark:text-gray-400 text-sm mb-4">
                Create custom workflows by selecting from a library of data processing tasks
              </p>
              <div className="space-y-2 text-xs text-windows-500 dark:text-gray-400 mb-4">
                <div className="flex items-center justify-center space-x-2">
                  <Database className="h-3 w-3 text-windows-500 dark:text-gray-400" />
                  <span>Data Ingestion</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <BarChart3 className="h-3 w-3 text-windows-500 dark:text-gray-400" />
                  <span>Data Processing</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <Shield className="h-3 w-3 text-windows-500 dark:text-gray-400" />
                  <span>Data Validation</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <Target className="h-3 w-3 text-windows-500 dark:text-gray-400" />
                  <span>Data Delivery</span>
                </div>
              </div>
              <button
                onClick={() => navigate('/create-pipeline')}
                className="btn-windows-primary flex items-center space-x-2 mx-auto"
              >
                <Plus size={16} />
                <span>Build Pipeline</span>
              </button>
            </div>
            
            <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-700">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg w-fit mx-auto mb-4">
                <Zap className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-windows-900 dark:text-gray-100 mb-2">Pre-built Templates</h3>
              <p className="text-windows-600 dark:text-gray-400 text-sm mb-4">
                Use ready-made pipeline templates for common data processing scenarios
              </p>
              <div className="space-y-2 text-xs text-windows-500 dark:text-gray-400 mb-4">
                <div className="flex items-center justify-center space-x-2">
                  <FileText className="h-3 w-3 text-windows-500 dark:text-gray-400" />
                  <span>ETL Pipeline</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <TestTube className="h-3 w-3 text-windows-500 dark:text-gray-400" />
                  <span>Data Quality</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <Activity className="h-3 w-3 text-windows-500 dark:text-gray-400" />
                  <span>Monitoring</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <Globe className="h-3 w-3 text-windows-500 dark:text-gray-400" />
                  <span>API Integration</span>
                </div>
              </div>
              <button
                onClick={() => navigate('/create-pipeline')}
                className="btn-windows flex items-center space-x-2 mx-auto"
              >
                <Eye size={16} />
                <span>Browse Templates</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Existing Runs */}
      <div className="card-windows">
        <div className="p-6 border-b border-windows-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-windows-900 dark:text-gray-100">Existing Tasks</h2>
          <p className="text-windows-600 dark:text-gray-400 mt-1">
            Click on any task to view details and manage your test data
          </p>
        </div>
        
        <div className="p-6">
          {state.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner-windows w-8 h-8"></div>
              <span className="ml-3 text-windows-600 dark:text-gray-400">Loading tasks...</span>
            </div>
          ) : runs.length === 0 ? (
            <div className="text-center py-12">
              <Database className="h-12 w-12 text-windows-400 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-windows-900 dark:text-gray-100 mb-2">No tasks found</h3>
              <p className="text-windows-600 dark:text-gray-400 mb-6">
                Get started by creating your first test data system
              </p>
              <button
                onClick={() => navigate('/')}
                className="btn-windows-primary flex items-center space-x-2 mx-auto"
              >
                <Plus size={16} />
                <span>Create First System</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {runs.map((run) => {
                const runId = run.run_id || run.id;
                const runIdNum = runId ? Number(runId.replace('run_', '').split('_')[0]) : null;
                // Navigate to flowchart page when clicking a run
                const navigatePath = `/run/${runId}`;
                
                return (
                  <div
                    key={runId}
                    className="card-windows p-6 hover:shadow-windows-lg cursor-pointer transition-all duration-200"
                    onClick={() => navigate(navigatePath)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-accent-100 dark:bg-accent-900/30 rounded-lg">
                          <Database className="h-6 w-6 text-accent-600 dark:text-accent-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-windows-900 dark:text-gray-100">Task {run.name || runId}</h3>
                          <p className="text-sm text-windows-600 dark:text-gray-400">Test Data System</p>
                          {runIdNum && taskDescriptions[runIdNum] && typeof taskDescriptions[runIdNum] === 'string' && (
                            <p className="text-xs text-windows-500 dark:text-gray-500 mt-1">
                              {String(taskDescriptions[runIdNum])}
                            </p>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-windows-400 dark:text-gray-500" />
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2 text-sm text-windows-600 dark:text-gray-400">
                        <Calendar size={14} />
                        <span>{formatDate(run.created_at || run.timestamp)}</span>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {getStatusBadges(run).map((badge, index) => (
                          <span
                            key={index}
                            className={`badge-windows badge-windows-${badge.color}`}
                          >
                            {badge.label}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex items-center justify-between pt-3 border-t border-windows-100 dark:border-gray-700">
                        <div className="flex items-center space-x-4 text-sm text-windows-600 dark:text-gray-400">
                          <div className="flex items-center space-x-1">
                            <FileText size={14} />
                            <span>{(run.has_schema || run.schema) ? 'Schema' : 'No Schema'}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <BarChart3 size={14} />
                            <span>{(run.has_synthetic_data || (run.synthetic_csv_files && run.synthetic_csv_files.length > 0)) ? 'Data' : 'No Data'}</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/run/${runId}`);
                          }}
                          className="btn-windows text-xs px-3 py-1"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>



      {/* Quick Actions */}
      <div className="card-windows">
        <div className="p-6 border-b border-windows-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-windows-900 dark:text-gray-100">Quick Actions</h2>
          <p className="text-windows-600 dark:text-gray-400 mt-1">
            Common tasks to get you started quickly
          </p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/')}
              className="card-windows p-6 text-left hover:shadow-windows-lg transition-all duration-200 group"
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-success-100 dark:bg-success-900/30 rounded-lg group-hover:bg-success-200 dark:group-hover:bg-success-900/50 transition-colors duration-200">
                  <Plus className="h-6 w-6 text-success-600 dark:text-success-400" />
                </div>
                <h3 className="font-semibold text-windows-900 dark:text-gray-100">Create New System</h3>
              </div>
              <p className="text-windows-600 dark:text-gray-400 text-sm">
                Start a new test data generation project with AI-powered analysis
              </p>
            </button>
            
            <button
              onClick={() => navigate('/overview')}
              className="card-windows p-6 text-left hover:shadow-windows-lg transition-all duration-200 group"
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-info-100 dark:bg-info-900/30 rounded-lg group-hover:bg-info-200 dark:group-hover:bg-info-900/50 transition-colors duration-200">
                  <FileText className="h-6 w-6 text-info-600 dark:text-info-400" />
                </div>
                <h3 className="font-semibold text-windows-900 dark:text-gray-100">Product Overview</h3>
              </div>
              <p className="text-windows-600 dark:text-gray-400 text-sm">
                Learn about features and capabilities of the Test Data Environment
              </p>
            </button>
            
            <button
              onClick={loadRuns}
              className="card-windows p-6 text-left hover:shadow-windows-lg transition-all duration-200 group"
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-warning-100 dark:bg-warning-900/30 rounded-lg group-hover:bg-warning-200 dark:group-hover:bg-warning-900/50 transition-colors duration-200">
                  <RefreshCw className="h-6 w-6 text-warning-600 dark:text-warning-400" />
                </div>
                <h3 className="font-semibold text-windows-900 dark:text-gray-100">Refresh Data</h3>
              </div>
              <p className="text-windows-600 dark:text-gray-400 text-sm">
                Update the list of available runs and their current status
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataWorkspacePage;

